import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Settings as SettingsIcon, Book, Send, LogOut, Users, Eye, EyeOff, Mail, Wrench, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global 401 interceptor — auto-redirect to login on expired token
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      console.warn('Auth token expired — redirecting to login');
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      // Set flag so login page shows expiry message
      localStorage.setItem('sessionExpired', 'true');
      // Brief delay so user sees the toast before redirect
      setTimeout(() => { window.location.href = '/'; }, 1500);
    }
    return Promise.reject(error);
  }
);

// Strip URLs from data before sending to voice AI (prevents AI from reading URLs aloud)
const stripUrlsForVoice = (data) => {
  const str = typeof data === 'string' ? data : JSON.stringify(data);
  return str.replace(/https?:\/\/[^\s"',}\]]+/g, '[link in log]');
};

const VoiceInterface = ({ onLogout }) => {
  const [voiceState, setVoiceState] = useState('idle'); // idle, listening, processing, speaking
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [voiceMode, setVoiceMode] = useState('tap'); // 'tap' or 'push'
  const [isConnected, setIsConnected] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(null);
  const [hasShownError, setHasShownError] = useState(false);
  const [branding, setBranding] = useState({ company_name: 'AI Voice', logo_url: '' });
  const [processedResponseIds, setProcessedResponseIds] = useState(new Set());
  const [isSessionActive, setIsSessionActive] = useState(false); // Track if voice session is active
  const [userName, setUserName] = useState(() => localStorage.getItem('userName') || '');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingGreeting, setPendingGreeting] = useState(false);
  const pendingGreetingRef = useRef(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', role: 'subscriber', subscriber_access: 'all', assigned_ticket: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState('subscriber');
  const [subscriberAccess, setSubscriberAccess] = useState('all');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [showServicePanel, setShowServicePanel] = useState(false);
  const [deviceUpdateForm, setDeviceUpdateForm] = useState({ device_id: '', battery_expiration: '', pad_expiration: '', notes: '' });
  
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);
  const isPushingRef = useRef(false);
  const localStreamRef = useRef(null);
  const micUnmuteTimerRef = useRef(null);
  const functionInProgressRef = useRef(false);
  const isSessionActiveRef = useRef(false);
  const voiceSessionIdRef = useRef(null);
  const voiceSessionStartRef = useRef(null);


  // Central 401 handler — redirect to login on expired token
  const handle401 = (error) => {
    if (error?.response?.status === 401) {
      console.warn('Auth token expired — redirecting to login');
      toast.error('Session expired. Please log in again.');
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      window.location.href = '/';
      return true;
    }
    return false;
  };


  // Proactive token expiry check — redirect before API calls fail
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000; // Convert to ms
        const now = Date.now();
        const minutesLeft = (expiresAt - now) / 60000;
        if (minutesLeft <= 0) {
          // Token already expired
          toast.error('Your session has expired. Please log in again.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('username');
          localStorage.setItem('sessionExpired', 'true');
          setTimeout(() => { window.location.href = '/'; }, 1500);
        } else if (minutesLeft <= 30) {
          // Warn user when < 30 minutes left
          toast.warning(`Your session expires in ${Math.round(minutesLeft)} minutes. Save your work.`);
        }
      } catch (e) {
        console.warn('Could not parse token expiry:', e);
      }
    };
    // Check on mount and every 5 minutes
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Check current user role and fetch tickets if needed
  useEffect(() => {
    const checkRole = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.post(`${API}/auth/verify`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const role = response.data.role || 'subscriber';
          setUserRole(role);
          setIsAdmin(role === 'admin');
          setSubscriberAccess(response.data.subscriber_access || 'all');
          
          // Fetch tickets for field tech
          if (role === 'field_tech') {
            try {
              const ticketRes = await axios.get(`${API}/tickets/my`, { headers: { Authorization: `Bearer ${token}` } });
              setMyTickets(ticketRes.data.tickets || []);
            } catch (e) { console.error('Error fetching tickets:', e); }
          }
          // Fetch all tickets for system admin
          if (role === 'admin') {
            try {
              const ticketRes = await axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } });
              setAllTickets(ticketRes.data.tickets || []);
            } catch (e) { console.error('Error fetching tickets:', e); }
          }
        } catch (error) {
          console.error('Error checking role:', error);
        }
      }
    };
    checkRole();
  }, []);

  // Fetch users for admin panel
  const fetchUsers = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  // Send communications log via email
  const handleEmailLog = async () => {
    if (messages.length === 0) {
      toast.error('No messages to send');
      return;
    }
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      toast.error('No email address on file. Ask your admin to add your email.');
      return;
    }
    setIsSendingEmail(true);
    const token = localStorage.getItem('authToken');
    const loggedInUser = localStorage.getItem('userName') || userName || 'Unknown';
    try {
      const logEntries = messages.filter(m => !m.isLive).map(m => ({
        role: m.role,
        content: m.content || '',
        imageUrl: m.imageUrl || '',
        timestamp: m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ''
      }));
      await axios.post(`${API}/email/send-log`, {
        username: loggedInUser,
        recipient_email: userEmail,
        log_entries: logEntries
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Communications log sent to ${userEmail}`);
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error('Username and password are required');
      return;
    }
    
    const token = localStorage.getItem('authToken');
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User '${newUser.username}' created`);
      setNewUser({ username: '', password: '', email: '', role: 'subscriber', subscriber_access: 'all', assigned_ticket: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Update user
  const handleUpdateUser = async (userId) => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`${API}/admin/users/${userId}`, editingUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user '${username}'?`)) return;
    
    const token = localStorage.getItem('authToken');
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User '${username}' deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Field Tech: Update ticket status
  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.put(`${API}/tickets/${ticketId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ticket ${ticketId} → ${newStatus}`);
      const res = await axios.get(`${API}/tickets/my`, { headers: { Authorization: `Bearer ${token}` } });
      setMyTickets(res.data.tickets || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    }
  };

  // Field Tech: Submit device maintenance update
  const handleDeviceUpdate = async () => {
    if (!deviceUpdateForm.device_id) { toast.error('Device ID is required'); return; }
    if (!deviceUpdateForm.battery_expiration && !deviceUpdateForm.pad_expiration) { toast.error('Enter at least one expiration date'); return; }
    const token = localStorage.getItem('authToken');
    try {
      const res = await axios.put(`${API}/device/${encodeURIComponent(deviceUpdateForm.device_id)}/update-maintenance`, {
        battery_expiration: deviceUpdateForm.battery_expiration || null,
        pad_expiration: deviceUpdateForm.pad_expiration || null,
        notes: deviceUpdateForm.notes || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setDeviceUpdateForm({ device_id: '', battery_expiration: '', pad_expiration: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update device');
    }
  };

  // Function to convert URLs in text to clickable links (handles markdown links and bare URLs)
  const renderMessageWithLinks = (text) => {
    if (!text) return '';
    
    // Combined regex: first match markdown [text](url), then bare URLs (allow commas for Google Maps coords)
    const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s<>)]+)/g;
    const elements = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before this match
      if (match.index > lastIndex) {
        elements.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
      }
      if (match[1] && match[2]) {
        // Markdown link: [text](url)
        elements.push(
          <a key={key++} href={match[2]} target="_blank" rel="noopener noreferrer"
            className="text-cyan-400 hover:text-cyan-300 underline"
            onClick={(e) => e.stopPropagation()}>
            {match[1]}
          </a>
        );
      } else {
        // Bare URL
        const url = match[3].replace(/[.,;:!?]+$/, ''); // strip trailing punctuation
        elements.push(
          <a key={key++} href={url} target="_blank" rel="noopener noreferrer"
            className="text-cyan-400 hover:text-cyan-300 underline break-all"
            onClick={(e) => e.stopPropagation()}>
            {url.length > 60 ? url.slice(0, 60) + '...' : url}
          </a>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(<span key={key++}>{text.slice(lastIndex)}</span>);
    }
    return elements.length > 0 ? elements : text;
  };

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    messagesRef.current = messages;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  // Save voice session data when user closes/navigates away
  useEffect(() => {
    const saveSessionOnExit = () => {
      if (voiceSessionIdRef.current && voiceSessionStartRef.current) {
        const durationSeconds = (Date.now() - voiceSessionStartRef.current) / 1000;
        const authToken = localStorage.getItem('authToken');
        const sessionMessages = messagesRef.current
          .filter(m => !m.isLive)
          .map(m => ({ role: m.role, content: m.content || '' }));
        if (authToken) {
          const payload = JSON.stringify({
            session_id: voiceSessionIdRef.current,
            duration_seconds: durationSeconds,
            conversation: sessionMessages
          });
          // Use sendBeacon for reliable delivery on page unload
          const url = `${API}/usage/end-voice-session`;
          const blob = new Blob([payload], { type: 'application/json' });
          const headers = { type: 'application/json' };
          // sendBeacon doesn't support custom headers, so use fetch with keepalive as primary
          try {
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
              body: payload,
              keepalive: true
            });
          } catch (e) {
            // Fallback: at least try sendBeacon (won't have auth header but endpoint handles gracefully)
            navigator.sendBeacon?.(url, blob);
          }
          voiceSessionIdRef.current = null;
          voiceSessionStartRef.current = null;
        }
      }
    };
    window.addEventListener('beforeunload', saveSessionOnExit);
    window.addEventListener('pagehide', saveSessionOnExit);
    return () => {
      window.removeEventListener('beforeunload', saveSessionOnExit);
      window.removeEventListener('pagehide', saveSessionOnExit);
    };
  }, []);

  // Check if API key is configured on mount
  useEffect(() => {
    const checkApiKey = async () => {
      try {
        // Use the root API endpoint to check if backend is up, 
        // then trust that the OpenAI key is configured if the backend started successfully
        const response = await axios.get(`${API}/`);
        if (response.data?.status === 'ready') {
          setApiKeyConfigured(true);
        } else {
          setApiKeyConfigured(false);
        }
      } catch (error) {
        // Retry once after a short delay
        setTimeout(async () => {
          try {
            const retryResponse = await axios.get(`${API}/`);
            setApiKeyConfigured(retryResponse.data?.status === 'ready');
          } catch {
            setApiKeyConfigured(false);
          }
        }, 2000);
      }
    };
    checkApiKey();
  }, []);

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding({
          company_name: response.data.company_name || 'AI Voice',
          logo_url: response.data.logo_url || 'https://customer-assets.emergentagent.com/job_vocalguru-4/artifacts/kdka61gz_Generated%20Image%20December%2016%2C%202025%20-%205_41AM.jpeg'
        });
      } catch (error) {
        console.error('Error loading branding:', error);
      }
    };
    loadBranding();
  }, []);

  // Initialize WebRTC connection
  const initializeVoiceConnection = async () => {
    try {
      // Check if OpenAI API key is configured and get session with transcription enabled
      const response = await axios.post(`${API}/v1/realtime/session`, {}, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.data.client_secret?.value) {
        if (!hasShownError) {
          toast.error('OpenAI API key not configured. Please add your key to enable voice features.', { duration: 5000 });
          setHasShownError(true);
        }
        return;
      }

      // Get ephemeral token
      const ephemeralKey = response.data.client_secret.value;
      console.log('Got ephemeral key, setting up WebRTC...');

      // Track voice session start
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        axios.post(`${API}/usage/track-voice-session`, {}, { headers: { Authorization: `Bearer ${authToken}` } })
          .then(res => {
            if (res.data?.session_id) {
              voiceSessionIdRef.current = res.data.session_id;
              voiceSessionStartRef.current = Date.now();
            }
          })
          .catch(() => {});
      }

      // Create peer connection with STUN servers for mobile compatibility
      const config = {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      };
      peerConnectionRef.current = new RTCPeerConnection(config);
      
      // Setup audio element with mobile-specific attributes
      if (!audioElementRef.current) {
        audioElementRef.current = document.createElement('audio');
        audioElementRef.current.autoplay = true;
        audioElementRef.current.muted = isMuted;
        // iOS Safari requires playsinline attribute
        audioElementRef.current.setAttribute('playsinline', 'true');
        audioElementRef.current.setAttribute('webkit-playsinline', 'true');
        // Set audio to play through speaker on mobile
        audioElementRef.current.setAttribute('x-webkit-airplay', 'allow');
        document.body.appendChild(audioElementRef.current);
      } else {
        // Clear stale srcObject from any previous session to prevent
        // the click handler's play() from briefly replaying old cached audio
        audioElementRef.current.srcObject = null;
      }

      // Handle incoming audio — guard against multiple ontrack calls
      // On iOS Chrome, reassigning srcObject restarts audio playback,
      // causing an "echo" at the start of the first response.
      let trackAttached = false;
      peerConnectionRef.current.ontrack = (event) => {
        if (trackAttached) {
          console.log('Ignoring duplicate ontrack event');
          return;
        }
        trackAttached = true;
        console.log('Received audio track — attaching to audio element');
        audioElementRef.current.srcObject = event.streams[0];
      };

      // === CONNECTION STABILITY MONITORING ===
      // Monitor ICE connection state — detect drops and auto-recover
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        const state = peerConnectionRef.current?.iceConnectionState;
        console.log('ICE connection state:', state);
        if (state === 'disconnected' || state === 'failed') {
          console.warn('WebRTC connection lost — cleaning up');
          toast.error('Voice connection lost. Tap mic to reconnect.');
          // Full cleanup
          if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.srcObject = null;
          }
          stopMicrophone();
          if (micUnmuteTimerRef.current) {
            clearTimeout(micUnmuteTimerRef.current);
            micUnmuteTimerRef.current = null;
          }
          functionInProgressRef.current = false;
          if (peerConnectionRef.current) {
            peerConnectionRef.current.close();
            peerConnectionRef.current = null;
          }
          dataChannelRef.current = null;
          setIsSessionActive(false); isSessionActiveRef.current = false;
          setIsConnected(false);
          setVoiceState('idle');
        }
      };

      peerConnectionRef.current.onconnectionstatechange = () => {
        const state = peerConnectionRef.current?.connectionState;
        console.log('Peer connection state:', state);
        if (state === 'failed' || state === 'closed') {
          console.warn('Peer connection closed/failed');
          setIsConnected(false);
          setIsSessionActive(false); isSessionActiveRef.current = false;
          setVoiceState('idle');
        }
      };

      // Get user microphone with mobile-optimized constraints
      const audioConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Lower sample rate for better mobile compatibility
          sampleRate: { ideal: 16000 },
          channelCount: { ideal: 1 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      localStreamRef.current = stream;
      stream.getTracks().forEach(track => {
        peerConnectionRef.current.addTrack(track, stream);
      });

      // Setup data channel
      dataChannelRef.current = peerConnectionRef.current.createDataChannel('oai-events');
      dataChannelRef.current.onmessage = handleDataChannelMessage;
      
      // Monitor data channel health
      dataChannelRef.current.onclose = () => {
        console.warn('Data channel closed unexpectedly');
        if (isSessionActiveRef.current) {
          toast.error('Voice session ended. Tap mic to reconnect.');
          setIsSessionActive(false); isSessionActiveRef.current = false;
          setIsConnected(false);
          setVoiceState('idle');
        }
      };
      dataChannelRef.current.onerror = (event) => {
        console.error('Data channel error:', event);
      };
      
      // Send session update to enable transcription after connection
      dataChannelRef.current.onopen = () => {
        console.log('Data channel opened');
        // IMMEDIATELY mute mic to prevent server VAD from auto-triggering a response
        // during setup. Without this, VAD picks up ambient noise and generates a 
        // concurrent response alongside our explicit greeting = "echo" on iOS Chrome.
        // The mic is unmuted after the greeting response.done fires.
        if (localStreamRef.current && pendingGreetingRef.current) {
          localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = false; });
          console.log('Mic muted during greeting setup to prevent VAD echo');
        }
        // Configure session to enable input audio transcription and knowledge base function
        const sessionUpdate = {
          type: 'session.update',
          session: {
            type: 'realtime',
            instructions: 'Your name is AEDA, which stands for AED Assistant. You are a voice assistant for Cardiac Solutions that monitors AED devices. Speak in a clear, professional, and friendly tone. VOICE-TEXT CONSISTENCY RULE: Your spoken audio MUST exactly match your text output word for word. Never say different numbers, names, or values aloud than what appears in your text. Do not ad-lib, paraphrase, or add filler words. If the data says 48, say forty-eight — never round or estimate. Keep responses SHORT and precise — no more than 2-3 sentences per topic unless the user asks for details. Fun fact about you: your favorite football team is the University of Alabama Crimson Tide. If anyone asks about your favorite football team, enthusiastically say "ROLL TIDE!!" first, then you can chat about Bama. You are a proud Bama fan through and through. SUBSCRIBER ALIASES: If a user says "Franklin County Sheriff" or "Sheriff of Franklin County" or any variation, they mean the subscriber named "County of Franklin". Always use "County of Franklin" when querying data for them. You have access to real-time AED data through function tools. CRITICAL RULE: NEVER speak, read out, or include any URL or web link in your voice responses. If data includes a URL or download link, just say the information is displayed in the communications log — do NOT read the URL characters. IMPORTANT DATA FIELD DEFINITIONS: expired_batteries = batteries that HAVE ALREADY expired. expired_pads = pads that HAVE ALREADY expired. expiring_batt_pads = batteries and/or pads that will expire within the next 90 days (expiring soon, NOT yet expired). expired_bp = combined total of expired batteries and pads. CRITICAL PRESENTATION RULES: 1) ALWAYS say "batteries and pads" together as one combined category — NEVER separate them into "batteries" and "pads" individually. The counts for batteries and pads are always equal — if expired_bp is 24 that means 24 expired batteries AND 24 expired pads. If expiring_batt_pads is 10 that means 10 expiring batteries AND 10 expiring pads. So say "24 expired batteries and pads" or "10 expiring batteries and pads." 2) NEVER confuse expired with expiring. Expired means ALREADY past expiration. Expiring means will expire within 90 days but has NOT expired yet. These are completely different categories — do not mix them up or present expiring items as expired. Intent routing: IMPORTANT ROUTING RULES — follow these exactly: 1) If the user mentions a SPECIFIC subscriber name, organization, company, or uses an alias like "Franklin County Sheriff" or "GPC" or "Altec", ALWAYS use query_subscriber_status — NEVER use query_system_status for subscriber-specific questions. 2) Only use query_system_status for system-WIDE STATUS questions with NO specific subscriber mentioned (e.g. "what is the system status", "how many total AEDs", "how many expired batteries"). NEVER use query_system_status for trend questions. Use include_detailed_counts=true if asking about batteries/pads/expiring/expired. TREND QUERIES: When a user asks for a "trend", "how are we trending", or "compare to yesterday" — for system-wide trends, call query_system_trend (NOT query_system_status). For a specific subscriber trend, call query_subscriber_trend (NOT query_subscriber_status). These dedicated trend tools return ONLY the yesterday vs today comparison — just read it as given. The system status data includes yesterday\'s percent ready and the change from yesterday — use this to answer comparison questions like "how does today compare to yesterday" or "what was yesterday\'s status". CRITICAL: You DO have access to yesterday\'s system status AND subscriber status. NEVER say you don\'t have historical data. When the user asks about yesterday, trends, or comparisons, ALWAYS call the appropriate tool first — the response contains yesterday\'s percent ready and the delta. When presenting system status, follow this EXACT order. Read the numbers EXACTLY from the data — do NOT calculate, estimate, or round any values: 1) adjusted percent ready — ALWAYS use the adjusted_percent_ready field from the data and say the word "adjusted" before "percent ready" every time. Format as XXX.X% (e.g. 83.3%, 95.0%, 100.0%), 2) total AEDs — use the exact total_aeds field, 3) total ready — use the exact Ready count. Then ONLY mention categories that have a count greater than zero (not ready, reposition, lost contact, expired batteries and pads, expiring batteries and pads, not present, unknown). For zero-count categories, simply say "no expired or expiring batteries and pads" or similar — do NOT list each zero category individually. NEVER make up or estimate numbers. Use ONLY the exact values from the API response. If the user asks what can be done to improve the percentage of not ready or how to improve readiness, respond with this guidance: "You should focus on percent ready and work toward a goal of as high in the 90s as possible. The biggest issue is expired batteries and pads, with GPC having the most. Either contact them directly or send field techs to replace as many of these as possible. The next big issues are reposition and lost contact. Send emails to all subscribers with reposition issues and send field techs to resolve lost contacts. Monitor your percent ready daily or weekly and focus on making this number go up." For a specific subscriber/organization, use query_subscriber_status. The response includes expiry_device_hints with lists of specific devices that have expired or expiring batteries and pads — use this to answer questions like "which devices have expired batteries and pads" by listing the device IDs and locations. For a specific device ID (like FZDYM-S11), use query_device_status. EXPIRATION DATE QUERIES: When a user asks about expiration dates, battery expiration, or pad expiration for a device, use query_device_status. If device IDs were mentioned in your PREVIOUS response (e.g. from subscriber status showing devices with expiring or expired batteries and pads), use those IDs automatically — do NOT ask the user to repeat them. You can call query_device_status multiple times if multiple devices were listed. If NO device IDs are available from context, ask the user to spell the AED ID using NATO phonetics. IMPORTANT: When a user needs to provide a specific device ID or AED ID by voice, ask them to spell it out using the NATO phonetic alphabet (Alpha, Bravo, Charlie, Delta, Echo, Foxtrot, Golf, Hotel, India, Juliet, Kilo, Lima, Mike, November, Oscar, Papa, Quebec, Romeo, Sierra, Tango, Uniform, Victor, Whiskey, X-ray, Yankee, Zulu). For example: "Please use phonetics to spell that device ID — like Foxtrot X-ray Delta Foxtrot Delta dash Mike zero three." LOST CONTACT DIAGNOSTIC PROTOCOL: When a user asks about AED devices in Lost Contact status, after listing the device details, perform a diagnostic analysis for each device: 1) Check the camera battery level (battery_level_pct). If battery is LOW (below 30%), tell the user this is likely the problem — the battery died and the device can no longer communicate. 2) If battery level is OK (30% or above), check the cellular signal (cellular_signal_quality). If cellular signal is LOW or NONE, tell the user this may be the cause — poor cellular connectivity is preventing the device from reporting in. 3) If both battery and cellular appear normal, note that the cause is unclear and an on-site inspection is needed. 4) After providing the diagnostic analysis, ALWAYS proactively ask: Would you like me to create a service ticket to send a field tech to resolve this issue? For questions about service tickets, work orders, assigned tasks, or what work needs to be done today, use query_service_tickets. IMPORTANT: When you get service ticket results, give a brief summary ONLY — for example say "You have one open ticket for device ABC123 at GPC, a Lost Contact issue." Do NOT read out all the details, notes, parts, or location unless the user specifically asks for more details. For other questions, use query_knowledge_base. When you receive function results, answer the user\'s SPECIFIC question from the data — don\'t just read all the data, focus on what they asked. Keep responses clear and conversational. LOCATION REPORTS: When a user asks to send a report, email a report, or send the monthly report for a specific subscriber, use send_location_report with the subscriber name. The report will be emailed to the user\'s email address on file. Confirm that the report is being sent. NEARBY AED SEARCH: When a user asks about AEDs near a location — like do we have any AEDs near Valdosta, find AEDs close to Atlanta, any AEDs in 30501, are there AEDs in Georgia — you MUST call search_nearby_aeds. Do NOT try to answer from your own knowledge. You do NOT know AED locations — ALWAYS use the tool. After presenting results, offer Google Maps navigation. The links are in the communications log. NEVER read URLs aloud.',
            audio: {
              input: {
                format: { type: 'audio/pcm', rate: 24000 },
                transcription: { model: 'whisper-1' },
                turn_detection: {
                  type: 'server_vad',
                  threshold: 0.65,
                  prefix_padding_ms: 300,
                  silence_duration_ms: 800,
                  create_response: true,
                  interrupt_response: true
                }
              },
              output: {
                format: { type: 'audio/pcm', rate: 24000 },
                voice: 'shimmer',
                speed: 1.0
              }
            },
            tools: [
              {
                type: 'function',
                name: 'query_knowledge_base',
                description: 'Search the knowledge base for general questions not related to AED devices or subscriber status.',
                parameters: {
                  type: 'object',
                  properties: {
                    query: {
                      type: 'string',
                      description: 'The user\'s question to search for in the knowledge base'
                    }
                  },
                  required: ['query']
                }
              },
              {
                type: 'function',
                name: 'query_system_status',
                description: 'Get system-wide AED status. Use for ANY system-level question: system status, total AEDs, ready percentage, expired batteries, expiring batteries/pads, lost contact, not ready, any aggregate question about the fleet. Also use for system trend queries.',
                parameters: {
                  type: 'object',
                  properties: {
                    include_detailed_counts: {
                      type: 'boolean',
                      description: 'Set true when asking about expired/expiring batteries, pads, or detailed counts.'
                    },
                    trend_only: {
                      type: 'boolean',
                      description: 'Set true when user asks for trend, comparison to yesterday, or how things are trending. Returns ONLY yesterday vs today comparison, not full status.'
                    }
                  },
                  required: []
                }
              },
              {
                type: 'function',
                name: 'query_subscriber_status',
                description: 'Get AED device status for a SPECIFIC subscriber/organization. Use when the user mentions a specific subscriber name. Also use for subscriber trend queries.',
                parameters: {
                  type: 'object',
                  properties: {
                    subscriber: {
                      type: 'string',
                      description: 'The subscriber or organization name (e.g. "County of Franklin")'
                    },
                    trend_only: {
                      type: 'boolean',
                      description: 'Set true when user asks for trend, comparison to yesterday, or how things are trending. Returns ONLY yesterday vs today comparison, not full status.'
                    }
                  },
                  required: ['subscriber']
                }
              },
              {
                type: 'function',
                name: 'query_device_status',
                description: 'Get full details about a specific AED device by its ID. Use when the user asks about a specific device or sentinel ID (e.g. "FZDYM-S11").',
                parameters: {
                  type: 'object',
                  properties: {
                    device_id: {
                      type: 'string',
                      description: 'The device sentinel ID (e.g. "FZDYM-S11")'
                    }
                  },
                  required: ['device_id']
                }
              },
              {
                type: 'function',
                name: 'query_device_image',
                description: 'Get the last captured image/photo of a specific AED device. Use when user asks to see a device, show the image, last photo, or picture of a device.',
                parameters: {
                  type: 'object',
                  properties: {
                    device_id: {
                      type: 'string',
                      description: 'The device sentinel ID (e.g. "FZDYM-S11")'
                    }
                  },
                  required: ['device_id']
                }
              },
              {
                type: 'function',
                name: 'query_device_location',
                description: 'Get the physical location of a specific AED device. Use when user asks where a device is located, its placement, building, or site.',
                parameters: {
                  type: 'object',
                  properties: {
                    device_id: {
                      type: 'string',
                      description: 'The device sentinel ID (e.g. "FZDYM-S11")'
                    }
                  },
                  required: ['device_id']
                }
              },
              {
                type: 'function',
                name: 'query_system_trend',
                description: 'Get the system trend — yesterday vs today percent ready comparison. Use when user asks about system trend, how the system is trending, or comparing today to yesterday.',
                parameters: {
                  type: 'object',
                  properties: {},
                  required: []
                }
              },
              {
                type: 'function',
                name: 'query_subscriber_trend',
                description: 'Get a specific subscriber trend — yesterday vs today percent ready comparison. Use when user asks about a subscriber trend or how a subscriber is trending.',
                parameters: {
                  type: 'object',
                  properties: {
                    subscriber: {
                      type: 'string',
                      description: 'The subscriber or organization name (e.g. "County of Franklin")'
                    }
                  },
                  required: ['subscriber']
                }
              },
              {
                type: 'function',
                name: 'query_totals_expired',
                description: 'Get the total count of expired batteries and pads across all subscribers. Use when user asks about total expired batteries or pads system-wide.',
                parameters: {
                  type: 'object',
                  properties: {},
                  required: []
                }
              },
              {
                type: 'function',
                name: 'query_totals_expiring',
                description: 'Get the total count of batteries/pads expiring within 90 days across all subscribers. Use when user asks about expiring batteries or pads.',
                parameters: {
                  type: 'object',
                  properties: {},
                  required: []
                }
              },
              {
                type: 'function',
                name: 'query_service_tickets',
                description: 'Get the current user\'s service tickets. Use when the user asks about their tickets, work orders, service assignments, or scheduled work.',
                parameters: {
                  type: 'object',
                  properties: {},
                  required: []
                }
              },
              {
                type: 'function',
                name: 'query_subscriber_devices_by_status',
                description: 'Get details about specific devices for a subscriber filtered by status. Use when user asks for details about devices in a certain status, like "show me the lost contact devices for Altec" or "which GPC devices have expired batteries and pads". Valid statuses: Ready, Not Ready, Reposition, Lost Contact, Expired B/P, Expiring B/P, Not Present, Unknown.',
                parameters: {
                  type: 'object',
                  properties: {
                    subscriber: { type: 'string', description: 'Subscriber name' },
                    status: { type: 'string', description: 'Device status filter, e.g. "Lost Contact", "Expired B/P", "Reposition"' }
                  },
                  required: ['subscriber', 'status']
                }
              },
              {
                type: 'function',
                name: 'query_devices_by_model',
                description: 'Get status summary and details of all AED devices of a specific model. ALWAYS use when user asks about a specific AED model like "status of all HeartSine 360P" or "how many 350P do we have" or "show me all Samaritan devices". Matches model names partially.',
                parameters: {
                  type: 'object',
                  properties: {
                    subscriber: { type: 'string', description: 'Subscriber name or "all" to search all accessible subscribers' },
                    model: { type: 'string', description: 'Model name or partial match, e.g. "360P", "HeartSine", "Samaritan PAD"' }
                  },
                  required: ['subscriber', 'model']
                }
              },
              {
                type: 'function',
                name: 'update_ticket_status',
                description: 'Update the status of a service ticket. Use when the user says they want to acknowledge dispatch, are enroute, are onsite, or have completed the work. Valid statuses: acknowledged, enroute, onsite, completed.',
                parameters: {
                  type: 'object',
                  properties: {
                    ticket_id: {
                      type: 'string',
                      description: 'The ticket ID (e.g. "TKT-2026-0415"). If user doesn\'t specify, use the most recent active ticket.'
                    },
                    status: {
                      type: 'string',
                      description: 'New status: "acknowledged", "enroute", "onsite", or "completed"',
                      enum: ['acknowledged', 'enroute', 'onsite', 'completed']
                    }
                  },
                  required: ['ticket_id', 'status']
                }
              },
              {
                type: 'function',
                name: 'log_ticket_work',
                description: 'Log work performed on a service ticket, such as replacing a camera, battery, or pads. Use when the user says they replaced a part, installed something, or performed any maintenance action.',
                parameters: {
                  type: 'object',
                  properties: {
                    ticket_id: {
                      type: 'string',
                      description: 'The ticket ID (e.g. "TKT-2026-0415"). If user doesn\'t specify, use the most recent active ticket.'
                    },
                    action: {
                      type: 'string',
                      description: 'Type of work: "replaced_camera", "replaced_battery", "replaced_pads", "other"'
                    },
                    description: {
                      type: 'string',
                      description: 'Description of the work performed'
                    },
                    new_part_id: {
                      type: 'string',
                      description: 'The new part ID if a part was replaced (e.g. "XYZ987")'
                    }
                  },
                  required: ['ticket_id', 'action', 'description']
                }
              },
              {
                type: 'function',
                name: 'create_service_ticket',
                description: 'Create a new service ticket to dispatch a field tech to resolve an issue with an AED device. Use when the user confirms they want to create a service ticket after a diagnostic analysis.',
                parameters: {
                  type: 'object',
                  properties: {
                    device_id: { type: 'string', description: 'The sentinel ID of the device' },
                    subscriber: { type: 'string', description: 'The subscriber name' },
                    issue_type: { type: 'string', description: 'Type of issue, e.g. Lost Contact, Expired B/P' },
                    description: { type: 'string', description: 'Description of the issue and recommended action' }
                  },
                  required: ['device_id', 'subscriber', 'issue_type', 'description']
                }
              },
              {
                type: 'function',
                name: 'list_subscribers_by_aed_count',
                description: 'List all subscribers ranked by total number of AEDs, from most to least. Use when the user asks which subscribers have the most AEDs, or wants a ranked list of subscribers.',
                parameters: { type: 'object', properties: {}, required: [] }
              },
              {
                type: 'function',
                name: 'send_location_report',
                description: 'Send a location export report via email for a specific subscriber. Use when the user says send me a report, email me the report, send the monthly report, or any variation requesting a report for a subscriber.',
                parameters: { type: 'object', properties: { subscriber: { type: 'string', description: 'The subscriber name to generate the report for' } }, required: ['subscriber'] }
              },
              {
                type: 'function',
                name: 'search_nearby_aeds',
                description: 'ALWAYS use this tool when the user mentions AEDs near a place, AEDs in a city, AEDs close to somewhere, or asks if there are any AEDs in or near any location, city, zip code, or address. Examples: any AEDs near Valdosta, do we have AEDs in Atlanta, find AEDs close to 30501, are there AEDs near me in Georgia. This searches the AED database by location.',
                parameters: { type: 'object', properties: { location: { type: 'string', description: 'City, address, or zip code to search near' }, radius_miles: { type: 'integer', description: 'Search radius in miles, default 25' }, limit: { type: 'integer', description: 'Max results, default 5' } }, required: ['location'] }
              }
            ],
            tool_choice: 'auto'
          }
        };
        dataChannelRef.current.send(JSON.stringify(sessionUpdate));
        console.log('Sent session update with transcription and knowledge base function enabled');
        
        // Send greeting after a short delay to ensure session.update is processed
        setTimeout(() => {
          if (pendingGreetingRef.current) {
            sendGreeting();
            pendingGreetingRef.current = false;
            setPendingGreeting(false);
          } else {
            // No greeting pending (e.g. push-to-talk reconnect) — ensure mic is unmuted
            if (localStreamRef.current) {
              localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = true; });
            }
          }
        }, 300);
      };

      // Create and send offer
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      console.log('SDP offer created, negotiating with OpenAI...');

      // Send offer through backend proxy (avoids browser body stream issues)
      const negotiateResponse = await axios.post(
        `${API}/v1/realtime/negotiate?token=${encodeURIComponent(ephemeralKey)}`,
        offer.sdp,
        {
          headers: { 'Content-Type': 'application/sdp' },
          transformResponse: [data => data],  // Keep raw SDP text
          timeout: 15000
        }
      );

      const answerSdp = negotiateResponse.data;
      console.log('SDP answer received, length:', answerSdp.length);

      const answer = {
        type: 'answer',
        sdp: answerSdp
      };

      await peerConnectionRef.current.setRemoteDescription(answer);
      console.log('WebRTC connection established successfully');
      setIsConnected(true);
      setApiKeyConfigured(true);
      toast.success('Voice connection established!');
    } catch (error) {
      console.error('Failed to initialize voice connection:', error);
      if (!hasShownError) {
        if (error.response?.status === 404) {
          toast.error('Voice features require OpenAI API key. Add it to backend .env file and restart.', { duration: 5000 });
        } else {
          toast.error('Failed to connect. Check your microphone permissions.');
        }
        setHasShownError(true);
      }
    }
  };

  const handleDataChannelMessage = async (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('Received event:', data.type, data);
      
      // Handle speech started - user is speaking
      if (data.type === 'input_audio_buffer.speech_started') {
        console.log('User started speaking');
        setVoiceState('listening');
      }
      
      // Handle speech stopped
      if (data.type === 'input_audio_buffer.speech_stopped') {
        console.log('User stopped speaking');
        setVoiceState('processing');
      }
      
      // Handle input audio buffer committed - capture what was said
      if (data.type === 'input_audio_buffer.committed') {
        console.log('Audio buffer committed');
      }
      
      // Handle conversation item created for user input
      if (data.type === 'conversation.item.created' && data.item?.role === 'user') {
        console.log('User message item created:', data.item);
      }

      // Handle function calls for knowledge base queries
      if (data.type === 'response.function_call_arguments.done') {
        console.log('Function call requested:', data);
        const functionName = data.name;
        let args;
        try {
          args = JSON.parse(data.arguments);
        } catch (e) {
          args = {};
        }
        
        // Mark function in progress — prevents mic unmute during API calls
        functionInProgressRef.current = true;
        
        // Safe send helper — guards against closed data channel
        const safeSend = (payload) => {
          if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
            dataChannelRef.current.send(JSON.stringify(payload));
          } else {
            console.warn('Data channel not open — cannot send:', payload.type);
          }
        };
        
        // Send function error result helper
        const sendFunctionError = (callId, errorMsg) => {
          functionInProgressRef.current = false;
          safeSend({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output: errorMsg } });
          safeSend({ type: 'response.create' });
        };
        
        if (functionName === 'query_knowledge_base') {
          // Query the knowledge base
          console.log('Querying knowledge base with:', args.query);
          axios.post(`${API}/knowledge/query`, { query: args.query })
            .then(response => {
              console.log('Knowledge base response:', response.data);
              // Send function result back to the AI
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: JSON.stringify({
                    answer: response.data.answer,
                    sources: response.data.sources
                  })
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              
              // Request AI to generate response
              const responseCreate = {
                type: 'response.create'
              };
              dataChannelRef.current.send(JSON.stringify(responseCreate));
            })
            .catch(error => {
              console.error('Error querying knowledge base:', error);
              // Send error back to AI
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: JSON.stringify({
                    error: 'Failed to query knowledge base'
                  })
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
            });
        }

        if (functionName === 'query_subscriber_status' || functionName === 'query_subscriber_trend') {
          const subscriber = args.subscriber;
          // Detect trend intent from the user's latest message — bulletproof override
          const lastUserMsg = [...messagesRef.current].reverse().find(m => m.role === 'user');
          const isTrend = functionName === 'query_subscriber_trend' ||
            (lastUserMsg && /trend|trending|compare.*yesterday|yesterday.*compare|yesterday.*today/i.test(lastUserMsg.content));
          console.log('Querying subscriber status for:', subscriber, 'isTrend:', isTrend);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/subscriber/${encodeURIComponent(subscriber)}/status`, { headers, params: { include_expiry_device_hints: true } })
            .then(response => {
              const d = response.data;
              const counts = d.counts || {};
              const total = counts.monitored_aeds || 0;
              const ready = counts.ready || 0;
              const pct = total > 0 ? (ready / total * 100).toFixed(1) : '0.0';
              let formatted;
              
              if (isTrend) {
                // TREND ONLY — just yesterday vs today comparison
                const prevPct = d.prev_percent_ready != null ? Number(d.prev_percent_ready).toFixed(1) : 'N/A';
                const delta = d.percent_ready_delta_vs_yesterday || 0;
                const direction = delta > 0 ? 'improved (up)' : delta < 0 ? 'declined (down)' : 'unchanged';
                const issues = [
                  ['expired batteries and pads', counts.expired_bp || 0],
                  ['lost contact', counts.lost_contact || 0],
                  ['reposition', counts.reposition || 0],
                  ['expiring batteries and pads', counts.expiring_batt_pads || 0],
                ].filter(([, c]) => c > 0).slice(0, 2);
                formatted = `TREND for ${d.subscriber || subscriber} — say these numbers EXACTLY as written, including decimals:\n` +
                  `Yesterday: ${prevPct} adjusted percent ready\n` +
                  `Today: ${pct} adjusted percent ready\n` +
                  `Change: ${delta > 0 ? '+' : ''}${delta.toFixed(1)} percentage points — ${direction}\n`;
                if (issues.length > 0) {
                  formatted += `Top issues: ${issues.map(([l, c]) => `${c} ${l}`).join(', ')}`;
                }
              } else {
                // Full status
                formatted = 
                  `EXACT SUBSCRIBER STATUS for ${d.subscriber || subscriber} — read these numbers verbatim:\n` +
                  `Adjusted percent ready: ${pct}%\n` +
                  `Total AEDs: ${total}\n` +
                  `Total ready: ${ready}\n`;
                if (d.prev_percent_ready !== undefined && d.prev_percent_ready !== null) {
                  const delta = d.percent_ready_delta_vs_yesterday || 0;
                  const direction = delta > 0 ? 'improved (up)' : delta < 0 ? 'declined (down)' : 'unchanged';
                  formatted += `Yesterday's adjusted percent ready: ${Number(d.prev_percent_ready).toFixed(1)}%\n`;
                  formatted += `Change from yesterday: ${delta > 0 ? '+' : ''}${delta.toFixed(1)} percentage points — ${direction}\n`;
                }
                const subItems = [
                  ['Total not ready', counts.not_ready || 0],
                  ['Total reposition', counts.reposition || 0],
                  ['Total lost contact', counts.lost_contact || 0],
                  ['Total with expired batteries and pads', counts.expired_bp || 0],
                  ['Total with expiring batteries and pads', counts.expiring_batt_pads || 0],
                  ['Total not present', counts.not_present || 0],
                  ['Total unknown', counts.unknown || 0]
                ];
                const nonZero = subItems.filter(([, c]) => c > 0);
                const zeroLabels = subItems.filter(([, c]) => c === 0).map(([l]) => l);
                nonZero.forEach(([label, count]) => { formatted += `${label}: ${count}\n`; });
                if (zeroLabels.length > 0) { formatted += `All other categories (${zeroLabels.join(', ')}) are zero.`; }
                const hints = d.expiry_device_hints || {};
                if (hints.expired_bp_devices && hints.expired_bp_devices.length > 0) {
                  formatted += '\nDevices with expired batteries and pads: ' + hints.expired_bp_devices.map(dev => `${dev.sentinel_id} (${dev.site || ''}, ${dev.placement || ''})`).join(', ');
                }
                if (hints.expiring_batt_pads_devices && hints.expiring_batt_pads_devices.length > 0) {
                  formatted += '\nDevices with expiring batteries and pads: ' + hints.expiring_batt_pads_devices.map(dev => `${dev.sentinel_id} (${dev.site || ''}, ${dev.placement || ''})`).join(', ');
                }
              }
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: formatted }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              console.error('Error querying subscriber:', error);
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: JSON.stringify({ error: 'Failed to query subscriber status. The subscriber name may not exist.' })
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_system_status' || functionName === 'query_system_trend') {
          // Detect trend intent from the user's latest message — bulletproof override
          // since the AI often calls query_system_status even for trend questions
          const lastUserMsg = [...messagesRef.current].reverse().find(m => m.role === 'user');
          const isTrend = functionName === 'query_system_trend' || 
            (lastUserMsg && /trend|trending|compare.*yesterday|yesterday.*compare|yesterday.*today/i.test(lastUserMsg.content));
          console.log('Querying system status, isTrend:', isTrend);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/system-status`, { headers })
            .then(response => {
              const d = response.data;
              const statuses = {};
              (d.statuses || []).forEach(s => { statuses[s.status] = s; });
              const ready = statuses['Ready'] || {};
              const pct = (d.adjusted_percent_ready != null ? Number(d.adjusted_percent_ready) : (ready.percentage || 0)).toFixed(1);
              let formatted;
              
              if (isTrend) {
                // TREND ONLY — just yesterday vs today comparison
                const prevPct = d.prev_percent_ready != null ? Number(d.prev_percent_ready).toFixed(1) : 'N/A';
                const delta = d.percent_ready_delta_vs_yesterday || 0;
                const direction = delta > 0 ? 'improved (up)' : delta < 0 ? 'declined (down)' : 'unchanged';
                // Find top 2 non-zero issues for brief mention
                const issues = [
                  ['expired batteries and pads', (statuses['Expired B/P'] || {}).count || 0],
                  ['lost contact', (statuses['Lost Contact'] || {}).count || 0],
                  ['reposition', (statuses['Reposition'] || {}).count || 0],
                  ['not present', (statuses['Not Present'] || {}).count || 0],
                ].filter(([, c]) => c > 0).slice(0, 2);
                formatted = `SYSTEM TREND — say these numbers EXACTLY as written, including decimals:\n` +
                  `Yesterday: ${prevPct} adjusted percent ready\n` +
                  `Today: ${pct} adjusted percent ready\n` +
                  `Change: ${delta > 0 ? '+' : ''}${delta.toFixed(1)} percentage points — ${direction}\n`;
                if (issues.length > 0) {
                  formatted += `Top issues: ${issues.map(([l, c]) => `${c} ${l}`).join(', ')}`;
                }
              } else {
                // Full status
                formatted = 
                  `EXACT SYSTEM STATUS DATA — read these numbers verbatim:\n` +
                  `Adjusted percent ready: ${pct}%\n` +
                  `Total AEDs: ${d.total_aeds || 0}\n` +
                  `Total ready: ${ready.count || 0}\n`;
                if (d.prev_percent_ready !== undefined && d.prev_percent_ready !== null) {
                  const delta = d.percent_ready_delta_vs_yesterday || 0;
                  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'unchanged';
                  formatted += `Yesterday's adjusted percent ready: ${Number(d.prev_percent_ready).toFixed(1)}%\n`;
                  formatted += `Change from yesterday: ${direction === 'up' ? '+' : ''}${delta.toFixed(1)} percentage points (${direction})\n`;
                }
                const sysItems = [
                  ['Total not ready', (statuses['Not Ready'] || {}).count || 0],
                  ['Total reposition', (statuses['Reposition'] || {}).count || 0],
                  ['Total lost contact', (statuses['Lost Contact'] || {}).count || 0],
                  ['Total with expired batteries and pads', (statuses['Expired B/P'] || {}).count || 0],
                  ['Total with expiring batteries and pads', (statuses['Expiring B/P'] || {}).count || 0],
                  ['Total not present', (statuses['Not Present'] || {}).count || 0],
                  ['Total unknown', (statuses['Unknown'] || {}).count || 0]
                ];
                const sysNonZero = sysItems.filter(([, c]) => c > 0);
                const sysZeroLabels = sysItems.filter(([, c]) => c === 0).map(([l]) => l);
                sysNonZero.forEach(([label, count]) => { formatted += `${label}: ${count}\n`; });
                if (sysZeroLabels.length > 0) { formatted += `All other categories (${sysZeroLabels.join(', ')}) are zero.`; }
              }
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: formatted
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              console.error('Error querying system status:', error);
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: JSON.stringify({ error: 'Failed to query system status.' })
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_device_status') {
          const deviceId = args.device_id;
          console.log('Querying device status for:', deviceId);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/device/${encodeURIComponent(deviceId)}`, { headers })
            .then(response => {
              const d = response.data;
              const dev = d.device || {};
              let formatted;
              if (!dev || !dev.sentinel_id) {
                formatted = d.summary || `No AED found for ID ${deviceId}.`;
              } else {
                formatted = `DEVICE STATUS for ${dev.sentinel_id}:\n` +
                  `Subscriber: ${dev.subscriber || 'N/A'}\n` +
                  `Status: ${dev.status || 'N/A'} (${dev.detailed_status || ''})\n` +
                  `Site: ${dev.site || ''}, ${dev.building || ''}\n` +
                  `Placement: ${dev.placement || ''}\n` +
                  `Model: ${dev.model || ''}\n` +
                  `Battery expiration: ${dev.battery_expiration || 'N/A'}\n` +
                  `Pad expiration: ${dev.pad_expiration || 'N/A'}\n` +
                  `Camera battery level: ${dev.battery_level_pct != null ? dev.battery_level_pct + '%' : 'N/A'}\n` +
                  `Cellular signal: ${dev.cellular_signal_quality || 'N/A'} (${dev.cellular_signal_label || 'N/A'})\n` +
                  `Last seen: ${dev.last_seen_date || 'N/A'} (${dev.last_seen_days_ago || ''} days ago)\n` +
                  `Status explanation: ${dev.detailed_status_explanation || ''}`;
              }
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: formatted
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              console.error('Error querying device:', error);
              const detail = error.response?.data?.detail || 'Device not found.';
              const functionResult = {
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: data.call_id,
                  output: JSON.stringify({ error: typeof detail === 'string' ? detail : 'Device not found.' })
                }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }
        if (functionName === 'query_device_image') {
          const deviceId = args.device_id;
          console.log('Querying device image for:', deviceId);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/device/${encodeURIComponent(deviceId)}/last-image`, { headers })
            .then(response => {
              const d = response.data;
              const imageUrl = d.image?.download_url;
              // Give the AI a SHORT summary without the URL — voice can't read URLs
              const voiceSummary = d.summary 
                ? d.summary.replace(/https?:\/\/\S+/g, '').trim()
                : `Last image for ${d.sentinel_id || deviceId} has been retrieved. Captured at ${d.image?.captured_at || 'unknown time'}.`;
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ answer: voiceSummary + ' The image is now displayed in the communications log.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              // Show the image in the communications log
              if (imageUrl) {
                setMessages(prev => [...prev, { role: 'assistant', content: `Last image for ${d.sentinel_id || deviceId}:`, imageUrl, timestamp: new Date() }]);
              }
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve device image.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_device_location') {
          const deviceId = args.device_id;
          console.log('Querying device location for:', deviceId);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/device/${encodeURIComponent(deviceId)}/location`, { headers })
            .then(response => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: stripUrlsForVoice(response.data) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve device location.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_totals_expired') {
          console.log('Querying total expired');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/totals/expired`, { headers })
            .then(response => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: stripUrlsForVoice(response.data) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve expired totals.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_totals_expiring') {
          console.log('Querying total expiring');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/totals/expiring`, { headers })
            .then(response => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: stripUrlsForVoice(response.data) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve expiring totals.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_subscriber_devices_by_status') {
          const subscriber = args.subscriber;
          const status = args.status;
          console.log('Querying devices by status:', subscriber, status);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/subscriber/${encodeURIComponent(subscriber)}/devices`, { headers, params: { status } })
            .then(response => {
              const devices = response.data.devices || [];
              let formatted;
              if (devices.length === 0) {
                formatted = `No devices with status "${status}" found for ${subscriber}.`;
              } else {
                formatted = `EXACT DEVICE LIST for ${subscriber} with status "${status}" — ${devices.length} devices:\n`;
                devices.forEach(d => {
                  formatted += `\n- Device: ${d.sentinel_id} | Site: ${d.site || ''}, ${d.building || ''} | Placement: ${d.placement || ''} | Model: ${d.model || ''} | Battery exp: ${d.battery_expiration || 'N/A'} | Pad exp: ${d.pad_expiration || 'N/A'} | Camera battery level: ${d.battery_level_pct != null ? d.battery_level_pct + '%' : 'N/A'} | Cellular signal: ${d.cellular_signal_quality || 'N/A'} (${d.cellular_signal_label || 'N/A'}) | Last seen: ${d.last_seen_date || 'N/A'} (${d.last_seen_days_ago || ''} days ago)`;
                });
              }
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: formatted }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Failed to query devices by status.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'query_devices_by_model') {
          const subscriber = args.subscriber || 'all';
          const model = args.model || '';
          console.log('Querying devices by model:', subscriber, model);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          // For voice, query subscriber devices and filter by model client-side
          const fetchSub = sub => axios.get(`${API}/subscriber/${encodeURIComponent(sub)}/devices`, { headers });
          const queryPromise = subscriber.toLowerCase() === 'all'
            ? axios.get(`${API}/subscribers/ranked`, { headers }).then(res => {
                const subs = (res.data.subscribers || []).slice(0, 10);
                return Promise.all(subs.map(s => fetchSub(s.subscriber).catch(() => ({ data: { devices: [] } }))));
              }).then(results => results.flatMap(r => r.data.devices || []))
            : fetchSub(subscriber).then(r => r.data.devices || []);
          queryPromise.then(devices => {
            const matched = devices.filter(d => (d.model || '').toLowerCase().includes(model.toLowerCase()));
            if (matched.length === 0) {
              const functionResult = { type: 'conversation.item.create', item: { type: 'function_call_output', call_id: data.call_id, output: `No devices matching model "${model}" found.` } };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              return;
            }
            const statusCounts = {};
            matched.forEach(d => { const s = d.detailed_status || 'Unknown'; statusCounts[s] = (statusCounts[s] || 0) + 1; });
            let formatted = `Found ${matched.length} devices matching "${model}":\nSTATUS BREAKDOWN:\n`;
            Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).forEach(([s, c]) => { formatted += `  ${s}: ${c}\n`; });
            formatted += `\nDEVICE LIST:\n`;
            matched.slice(0, 20).forEach(d => {
              formatted += `\n- ${d.subscriber || subscriber}: ${d.sentinel_id} | Status: ${d.detailed_status || ''} | Site: ${d.site || ''}, ${d.building || ''} | Battery exp: ${d.battery_expiration || 'N/A'} | Pad exp: ${d.pad_expiration || 'N/A'}`;
            });
            if (matched.length > 20) formatted += `\n\n... and ${matched.length - 20} more devices.`;
            const functionResult = { type: 'conversation.item.create', item: { type: 'function_call_output', call_id: data.call_id, output: formatted } };
            dataChannelRef.current.send(JSON.stringify(functionResult));
            dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
          }).catch(error => {
            console.error('Model query error:', error);
            const functionResult = { type: 'conversation.item.create', item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: `Could not query devices by model.` }) } };
            dataChannelRef.current.send(JSON.stringify(functionResult));
            dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
          });
        }

        if (functionName === 'query_service_tickets') {
          console.log('Querying service tickets');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/tickets/my`, { headers })
            .then(response => {
              const tickets = response.data.tickets || [];
              const output = tickets.length > 0
                ? JSON.stringify({ tickets: tickets.map(t => ({
                    ticket_id: t.ticket_id,
                    subscriber: t.subscriber,
                    status: t.status,
                    description: t.description,
                    device_id: t.device_id || null,
                    issue_type: t.issue_type || null,
                    priority: t.priority || null,
                    location: t.location || null,
                    parts_required: t.parts_required || [],
                    notes: (t.notes || []).map(n => ({ author: n.author, text: n.text }))
                  }))})
                : JSON.stringify({ message: 'No service tickets are currently assigned to you.' });
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve service tickets.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'update_ticket_status') {
          console.log('Updating ticket status via voice');
          const args = JSON.parse(data.arguments || '{}');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.put(`${API}/tickets/${encodeURIComponent(args.ticket_id)}/status`, { status: args.status }, { headers })
            .then(response => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify(response.data) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              // Refresh tickets in state
              axios.get(`${API}/tickets/my`, { headers }).then(r => setMyTickets(r.data.tickets || []));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: error.response?.data?.detail || 'Failed to update ticket status.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'log_ticket_work') {
          console.log('Logging ticket work via voice');
          const args = JSON.parse(data.arguments || '{}');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.post(`${API}/tickets/${encodeURIComponent(args.ticket_id)}/work-log`, {
            action: args.action,
            description: args.description,
            new_part_id: args.new_part_id || null,
            old_part_id: null
          }, { headers })
            .then(response => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify(response.data) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              // Refresh tickets in state
              axios.get(`${API}/tickets/my`, { headers }).then(r => setMyTickets(r.data.tickets || []));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: error.response?.data?.detail || 'Failed to log work.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'create_service_ticket') {
          console.log('Creating simulated service ticket via voice');
          const ticketNum = Math.floor(1000 + Math.random() * 9000);
          const ticketId = `TKT-2026-${ticketNum}`;
          const result = {
            success: true,
            ticket_id: ticketId,
            message: `Service ticket ${ticketId} has been created for device ${args.device_id} at ${args.subscriber}. Issue type: ${args.issue_type}. A field tech will be dispatched to resolve this issue.`,
            details: { device_id: args.device_id, subscriber: args.subscriber, issue_type: args.issue_type, description: args.description, status: 'dispatched' }
          };
          const functionResult = {
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify(result) }
          };
          dataChannelRef.current.send(JSON.stringify(functionResult));
          dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
        }

        if (functionName === 'list_subscribers_by_aed_count') {
          console.log('Listing subscribers by AED count');
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          axios.get(`${API}/subscribers/ranked`, { headers })
            .then(response => {
              const subs = response.data.subscribers || [];
              let formatted = `SUBSCRIBERS RANKED BY TOTAL AEDs (most first) — ${subs.length} subscribers:\n`;
              subs.forEach((s, i) => {
                formatted += `${i + 1}. ${s.subscriber}: ${s.total_aeds} AEDs (${s.ready} ready)\n`;
              });
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: formatted }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'Could not retrieve subscriber list.' }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }

        if (functionName === 'send_location_report') {
          console.log('Sending location report for:', args.subscriber);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          const userEmail = localStorage.getItem('userEmail') || '';
          const voiceUser = localStorage.getItem('userName') || '';
          if (!userEmail) {
            const functionResult = {
              type: 'conversation.item.create',
              item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: 'No email address on file for your account. Please ask your admin to add your email.' }) }
            };
            dataChannelRef.current.send(JSON.stringify(functionResult));
            dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
          } else {
            axios.post(`${API}/voice/tracey/location-export/email`, null, {
              headers,
              params: { subscriber: args.subscriber, to_email: userEmail, user_name: voiceUser }
            })
              .then(response => {
                const functionResult = {
                  type: 'conversation.item.create',
                  item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ success: true, message: `The location report for ${args.subscriber} has been sent to ${userEmail}.` }) }
                };
                dataChannelRef.current.send(JSON.stringify(functionResult));
                dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              })
              .catch(error => {
                console.error('Location report error:', error);
                const functionResult = {
                  type: 'conversation.item.create',
                  item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify({ error: `Failed to send the location report for ${args.subscriber}. Please try again.` }) }
                };
                dataChannelRef.current.send(JSON.stringify(functionResult));
                dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
              });
          }
        }
        if (functionName === 'search_nearby_aeds') {
          console.log('Searching nearby AEDs for:', args.location);
          const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
          const callId = data.call_id;
          axios.get(`${API}/aeds-near`, {
            headers,
            params: { location: args.location, radius_miles: args.radius_miles || 25, limit: args.limit || 5 }
          })
            .then(response => {
              const result = response.data;
              if (!result.ok || result.count === 0) {
                const functionResult = {
                  type: 'conversation.item.create',
                  item: { type: 'function_call_output', call_id: callId, output: JSON.stringify({ message: `No AEDs found near ${args.location}.` }) }
                };
                dataChannelRef.current.send(JSON.stringify(functionResult));
                dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
                return;
              }
              // Build result with voice_answer + navigation links
              let output = result.voice_answer || '';
              output += '\n\nAED DETAILS WITH NAVIGATION:\n';
              let logContent = '';
              (result.aeds || []).forEach(aed => {
                const mapsUrl = aed.latitude && aed.longitude ? `https://www.google.com/maps/dir/?api=1&destination=${aed.latitude},${aed.longitude}` : '';
                output += `\n- ${aed.subscriber}: ${aed.address || 'N/A'} | Device: ${aed.sentinel_id} | Status: ${aed.status || 'N/A'} | Distance: ${aed.distance_miles} miles | Google Maps: ${mapsUrl}`;
                logContent += `${aed.subscriber} — ${aed.address || 'N/A'}\nDevice: ${aed.sentinel_id} | Status: ${aed.status || 'N/A'} | ${aed.distance_miles} mi away\nNavigate: ${mapsUrl}\n\n`;
              });
              // Add AED details with clickable links directly to communications log
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: logContent.trim(),
                timestamp: new Date()
              }]);
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: callId, output }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            })
            .catch(error => {
              console.error('AED search error:', error);
              const functionResult = {
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: callId, output: JSON.stringify({ error: `Could not search for AEDs near ${args.location}.` }) }
              };
              dataChannelRef.current.send(JSON.stringify(functionResult));
              dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            });
        }
      }
      
      // Also handle the newer event format
      if (data.type === 'response.output_item.added' && data.item?.type === 'function_call') {
        console.log('Function call (output_item format):', data);
      }
      
      // Handle different event types - only process transcript events
      if (data.type === 'conversation.item.input_audio_transcription.completed') {
        // User finished speaking — add their message to the log
        console.log('User transcription completed:', data.transcript);
        const userTranscript = (data.transcript || '').trim();
        
        // Filter out Whisper phantom hallucinations (common when transcribing silence/noise)
        const WHISPER_PHANTOMS = [
          'silence', 'silence.', 'wow', 'wow.', 'thank you', 'thank you.',
          'thanks', 'thanks.', 'bye', 'bye.', 'you', 'you.', 'hmm', 'hmm.',
          'oh', 'oh.', 'uh', 'um', 'the end', 'the end.', 'okay', 'okay.',
          'so', 'so.', 'yeah', 'yeah.', 'yes', 'yes.', 'no', 'no.',
          'i\'m sorry', 'thank you for watching', 'thanks for watching',
          'please subscribe', 'see you next time', 'bye bye', 'bye-bye'
        ];
        if (!userTranscript || WHISPER_PHANTOMS.includes(userTranscript.toLowerCase())) {
          console.log('Filtered phantom Whisper transcription:', userTranscript);
          return; // Ignore phantom transcription — don't add to log or trigger AI response
        }
        
        // Add user message to communications log
        setMessages(prev => {
          const cleanedMessages = prev.filter(msg => !msg.isLive);
          return [...cleanedMessages, { role: 'user', content: userTranscript, timestamp: new Date() }];
        });
        
        // The Realtime API handles the response automatically via its tools and WebRTC audio
        setVoiceState('processing');
      } else if (data.type === 'conversation.item.input_audio_transcription.delta') {
        // Live user transcription - just log it
        console.log('User speaking:', data.delta);
        // Don't add to messages - wait for completed event
      } else if (data.type === 'response.audio_transcript.done') {
        // AI finished speaking — display the transcript (matches what was spoken)
        const responseId = data.response_id || data.item_id;
        console.log('AI transcript done:', data.transcript);
        if (!processedResponseIds.has(responseId)) {
          setProcessedResponseIds(prev => { const s = new Set(prev); s.add(responseId); return s; });
          setMessages(prev => {
            const filtered = prev.filter(msg => !(msg.role === 'assistant' && msg.isLive));
            if (filtered.some(msg => msg.role === 'assistant' && msg.content === data.transcript && !msg.isLive)) return filtered;
            return [...filtered, { role: 'assistant', content: data.transcript, timestamp: new Date(), responseId }];
          });
        }
      } else if (data.type === 'response.audio_transcript.delta') {
        // Live AI transcript — show what's being spoken in real-time
        const responseId = data.response_id || data.item_id;
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isLive && lastMessage.responseId === responseId) {
            return [...prev.slice(0, -1), { ...lastMessage, content: lastMessage.content + data.delta }];
          } else {
            return [...prev, { role: 'assistant', content: data.delta, timestamp: new Date(), isLive: true, responseId }];
          }
        });
      }
      
      // Handle response.created — AI is starting to respond
      if (data.type === 'response.created') {
        console.log('AI response started — muting mic to prevent interruption');
        // Clear function-in-progress flag since the new response has started
        functionInProgressRef.current = false;
        // Cancel any pending mic unmute timer from a previous response
        if (micUnmuteTimerRef.current) {
          clearTimeout(micUnmuteTimerRef.current);
          micUnmuteTimerRef.current = null;
        }
        setVoiceState('speaking');
        if (localStreamRef.current) {
          localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = false; });
        }
      }
      
      // Handle response.done — AI finished responding
      if (data.type === 'response.done') {
        console.log('AI response done — scheduling mic unmute');
        setVoiceState('idle');
        // Cancel any existing unmute timer first
        if (micUnmuteTimerRef.current) {
          clearTimeout(micUnmuteTimerRef.current);
        }
        // Do NOT unmute mic if a function call is being processed —
        // the function result will trigger a new response, and we don't
        // want VAD picking up noise between the two responses.
        if (functionInProgressRef.current) {
          console.log('Function in progress — skipping mic unmute');
        } else {
          micUnmuteTimerRef.current = setTimeout(() => {
            micUnmuteTimerRef.current = null;
            if (localStreamRef.current) {
              localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = true; });
              console.log('Mic unmuted after post-response delay');
            }
          }, 600);
        }
      }
      
      // Handle error events
      if (data.type === 'error') {
        console.error('OpenAI error:', JSON.stringify(data.error || data, null, 2));
        const errMsg = data.error?.message || '';
        // Suppress "active response in progress" — this is a timing issue, not a real error
        if (!errMsg.includes('active response in progress')) {
          toast.error(errMsg || 'Voice error occurred');
        }
        setVoiceState('idle');
      }
    } catch (error) {
      console.error('Error parsing data channel message:', error);
    }
  };

  const stopMicrophone = () => {
    // Track voice session end with duration and conversation
    if (voiceSessionIdRef.current && voiceSessionStartRef.current) {
      const durationSeconds = (Date.now() - voiceSessionStartRef.current) / 1000;
      const authToken = localStorage.getItem('authToken');
      // Collect conversation messages from this session
      const sessionMessages = messagesRef.current
        .filter(m => !m.isLive)
        .map(m => ({ role: m.role, content: m.content || '' }));
      if (authToken) {
        axios.post(`${API}/usage/end-voice-session`, 
          { session_id: voiceSessionIdRef.current, duration_seconds: durationSeconds, conversation: sessionMessages },
          { headers: { Authorization: `Bearer ${authToken}` } }
        ).catch(() => {});
      }
      voiceSessionIdRef.current = null;
      voiceSessionStartRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped microphone track');
      });
      localStreamRef.current = null;
    }
  };

  // Function to send greeting through the voice system
  const sendGreeting = () => {
    if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
      const hour = new Date().getHours();
      const timeGreeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
      const greetingText = userName 
        ? `${timeGreeting} ${userName}, how can I help you?`
        : `${timeGreeting}, how can I help you?`;
      
      // Send a response.create event to make the AI speak the greeting
      const greetingMessage = {
        type: 'response.create',
        response: {
          instructions: `Say exactly: "${greetingText}" in a friendly, warm tone. Do not add anything else.`
        }
      };
      dataChannelRef.current.send(JSON.stringify(greetingMessage));
      console.log('Sent greeting:', greetingText);
    } else {
      console.warn('Data channel not ready for greeting, retrying in 500ms');
      setTimeout(() => sendGreeting(), 500);
    }
  };

  const handleMicClick = async () => {
    // iOS audio unlock: call play() within user gesture to allow future autoplay.
    // srcObject is null here (cleared on session stop), so this fails silently
    // but still registers the user gesture for iOS WebKit.
    if (audioElementRef.current) {
      audioElementRef.current.play().catch(() => {});
    }

    if (voiceMode === 'tap') {
      // Tap-to-talk mode
      if (!isSessionActive) {
        // Check if user has set their name
        if (!userName) {
          setShowNamePrompt(true);
          return;
        }
        
        // Start listening - set session active immediately
        setIsSessionActive(true);
        isSessionActiveRef.current = true;
        setVoiceState('listening');
        setPendingGreeting(true); // Flag to send greeting after connection
        pendingGreetingRef.current = true;
        toast.info('Connecting...');
        
        // Always reinitialize the connection to ensure fresh microphone
        await initializeVoiceConnection();
      } else {
        // Stop the session
        console.log('Stopping session...');
        setIsSessionActive(false); isSessionActiveRef.current = false;
        setVoiceState('idle');
        setIsConnected(false); // Reset connection state
        toast.info('Stopped');
        
        // Stop the microphone
        stopMicrophone();
        
        // Cancel any pending mic unmute timer
        if (micUnmuteTimerRef.current) {
          clearTimeout(micUnmuteTimerRef.current);
          micUnmuteTimerRef.current = null;
        }
        
        // Cancel any in-progress AI response
        if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
          try {
            dataChannelRef.current.send(JSON.stringify({ type: 'response.cancel' }));
          } catch (e) {
            console.warn('Could not send response.cancel:', e);
          }
        }
        
        // Stop audio playback IMMEDIATELY — pause + clear stream
        if (audioElementRef.current) {
          audioElementRef.current.pause();
          audioElementRef.current.srcObject = null;
        }
        
        // Close the peer connection to fully reset
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
          peerConnectionRef.current = null;
        }
        if (dataChannelRef.current) {
          dataChannelRef.current = null;
        }
      }
    }
  };
  
  // Save user name to localStorage
  const saveUserName = (name) => {
    setUserName(name);
    localStorage.setItem('userName', name);
    setShowNamePrompt(false);
  };

  const handleMicMouseDown = async () => {
    // iOS Safari audio unlock
    if (audioElementRef.current) {
      audioElementRef.current.play().catch(() => {});
    }
    
    if (voiceMode === 'push') {
      if (!isConnected) {
        await initializeVoiceConnection();
      }
      isPushingRef.current = true;
      setVoiceState('listening');
      toast.info('Listening... Release to send');
    }
  };

  const handleMicMouseUp = () => {
    if (voiceMode === 'push' && isPushingRef.current) {
      isPushingRef.current = false;
      setVoiceState('processing');
      toast.info('Processing...');
      setTimeout(() => setVoiceState('idle'), 1500);
    }
  };

  const handleTextQuery = async () => {
    if (!textInput.trim()) return;

    const userMessage = textInput;
    setTextInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setVoiceState('processing');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API}/chat/query`, {
        message: userMessage,
        history: messagesRef.current.filter(m => !m.isLive).map(m => ({ role: m.role, content: m.content || '' }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const answer = response.data.answer || 'No response received.';
      const imageUrl = response.data.image_url || null;
      const aedLinks = response.data.aed_links || [];
      setMessages(prev => [...prev, { role: 'assistant', content: answer, imageUrl, timestamp: new Date() }]);
      // If AED navigation links were returned, add them as a separate log entry
      if (aedLinks.length > 0) {
        const linksContent = aedLinks.map(l => 
          `${l.label}\nDevice: ${l.device_id} | Status: ${l.status} | ${l.distance} mi\nNavigate: ${l.maps_url}`
        ).join('\n\n');
        setMessages(prev => [...prev, { role: 'assistant', content: linksContent, timestamp: new Date() }]);
      }
      // Refresh tickets if a ticket action was performed
      const lower = userMessage.toLowerCase();
      if (lower.includes('ticket') || lower.includes('acknowledge') || lower.includes('enroute') || lower.includes('onsite') || lower.includes('complete') || lower.includes('replaced')) {
        try {
          const ticketRes = await axios.get(`${API}/tickets/my`, { headers: { Authorization: `Bearer ${token}` } });
          setMyTickets(ticketRes.data.tickets || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Error querying:', error);
      toast.error('Failed to get response');
    } finally {
      setVoiceState('idle');
    }
  };


  const getMicButtonStyle = () => {
    const baseClasses = 'w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center transition-all duration-300';
    
    // Show red when session is active (listening/recording)
    if (isSessionActive) {
      return `${baseClasses} bg-gradient-to-br from-red-500 to-red-700 text-white glow-red`;
    }
    
    switch (voiceState) {
      case 'processing':
        return `${baseClasses} bg-gradient-to-br from-cyan-400 to-blue-600 text-white glow-blue-intense`;
      case 'speaking':
        return `${baseClasses} bg-gradient-to-br from-cyan-400 to-blue-500 text-white glow-blue`;
      default:
        return `${baseClasses} border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 arc-pulse`;
    }
  };

  const getMicAnimation = () => {
    if (isSessionActive) {
      return { scale: [1, 1.15, 1], transition: { repeat: Infinity, duration: 0.8 } };
    }
    switch (voiceState) {
      case 'processing':
        return { rotate: 360, transition: { repeat: Infinity, duration: 1.5, ease: 'linear' } };
      default:
        return {};
    }
  };

  // Pulsing animation for the mic icon
  const micIconPulse = {
    scale: [1, 1.2, 1],
    opacity: [0.8, 1, 0.8],
    transition: { 
      repeat: Infinity, 
      duration: 1.5,
      ease: "easeInOut"
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] flex flex-col h-screen overflow-hidden">
      {/* Name Prompt Modal */}
      {showNamePrompt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0d1520] border border-cyan-500/50 rounded-lg p-6 max-w-sm w-full"
          >
            <h2 className="text-xl font-stark text-cyan-400 mb-4">WELCOME</h2>
            <p className="text-cyan-300/80 mb-4">Please enter your name:</p>
            <input
              type="text"
              autoFocus
              className="w-full bg-[#0a0e14] border border-cyan-500/30 text-cyan-100 rounded-lg px-4 py-3 mb-4 focus:border-cyan-400 focus:outline-none"
              placeholder="Your name"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  saveUserName(e.target.value.trim());
                }
              }}
            />
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  const input = document.querySelector('input[placeholder="Your name"]');
                  if (input && input.value.trim()) {
                    saveUserName(input.value.trim());
                  }
                }}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-[#0a0e14]"
              >
                Continue
              </Button>
              <Button
                onClick={() => {
                  saveUserName('Friend');
                }}
                variant="outline"
                className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
              >
                Skip
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Stark-style Header */}
      <header className="border-b border-cyan-500/30 bg-gradient-to-r from-[#0a0e14] via-[#0d1520] to-[#0a0e14] px-4 py-4 flex-shrink-0 relative">
        {/* HUD corner accents */}
        <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-400/50"></div>
        <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-cyan-400/50"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-cyan-400/50"></div>
        <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-cyan-400/50"></div>
        
        {/* 3-column header layout: left icons | center branding | right icons */}
        <div className="flex items-center justify-between relative z-10">
          {/* Left: Admin icon + Field Tech Service + Device Dashboard */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isAdmin && (
              <>
              <button
                onClick={() => {
                  setShowUserManagement(true);
                  fetchUsers();
                }}
                className="text-cyan-400/70 hover:text-cyan-300 transition-colors p-2"
                title="User Management"
                data-testid="admin-settings-btn"
              >
                <Users className="h-5 w-5" />
              </button>
              <Link to="/usage" className="text-cyan-400/70 hover:text-cyan-300 transition-colors p-2" title="Usage Monitor" data-testid="usage-dashboard-btn">
                <BarChart3 className="h-5 w-5" />
              </Link>
              </>
            )}
            {userRole === 'field_tech' && (
              <button
                onClick={() => setShowServicePanel(true)}
                className="text-amber-400/70 hover:text-amber-300 transition-colors p-2"
                title="Service Tickets"
                data-testid="service-panel-btn"
              >
                <Wrench className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Center: Branding */}
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-base sm:text-2xl lg:text-3xl font-bold font-stark tracking-wider whitespace-nowrap" style={{ color: '#ff2d2d', textShadow: '0 0 20px rgba(255, 45, 45, 0.7), 0 0 40px rgba(255, 45, 45, 0.5), 0 0 60px rgba(255, 45, 45, 0.3)' }}>
              CARDIAC SOLUTIONS
            </h1>
          </div>
          
          {/* Right: Logout button */}
          <div className="w-10 flex-shrink-0 flex justify-end">
            <button
              onClick={() => {
                if (onLogout) {
                  onLogout();
                  toast.info('Logged out');
                }
              }}
              className="text-cyan-400/70 hover:text-cyan-300 transition-colors p-2"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* User Management Modal - Admin Only */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0d1520] border border-cyan-500/50 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-stark text-cyan-400 uppercase tracking-wider">User Management</h2>
              <button
                onClick={() => { setShowUserManagement(false); setShowNewPassword(false); setShowEditPassword(false); }}
                className="text-cyan-400/70 hover:text-cyan-300 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Add New User Form */}
            <div className="bg-[#0a0e14] border border-cyan-500/30 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-stark text-cyan-300 uppercase tracking-wider mb-4">Add New User</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="Username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                />
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-cyan-500/50 hover:text-cyan-300 transition-colors"
                    data-testid="toggle-new-password-visibility"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                  data-testid="new-user-email"
                />
                <div>
                  <label className="text-cyan-300/80 text-xs uppercase tracking-wider mb-1 block">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => {
                      const r = e.target.value;
                      setNewUser({
                        ...newUser,
                        role: r,
                        subscriber_access: r === 'system' ? 'all' : newUser.subscriber_access,
                        assigned_ticket: r === 'field_tech' ? newUser.assigned_ticket : ''
                      });
                    }}
                    className="w-full h-10 rounded-md border border-cyan-500/30 bg-[#0d1520] text-cyan-100 px-3 text-sm"
                    data-testid="new-user-role-select"
                  >
                    <option value="admin">Admin</option>
                    <option value="system">System</option>
                    <option value="subscriber">Subscriber</option>
                    <option value="field_tech">Field Tech</option>
                  </select>
                </div>
                {newUser.role === 'subscriber' && (
                  <Input
                    type="text"
                    placeholder="Subscriber name"
                    value={newUser.subscriber_access}
                    onChange={(e) => setNewUser({ ...newUser, subscriber_access: e.target.value })}
                    className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                    data-testid="new-user-subscriber-access"
                  />
                )}
                {newUser.role === 'field_tech' && (
                  <>
                    <Input
                      type="text"
                      placeholder="Subscriber name"
                      value={newUser.subscriber_access}
                      onChange={(e) => setNewUser({ ...newUser, subscriber_access: e.target.value })}
                      className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                    />
                    <div>
                      <label className="text-cyan-300/80 text-xs uppercase tracking-wider mb-1 block">Assign Ticket</label>
                      <select
                        value={newUser.assigned_ticket}
                        onChange={(e) => setNewUser({ ...newUser, assigned_ticket: e.target.value })}
                        className="w-full h-10 rounded-md border border-cyan-500/30 bg-[#0d1520] text-cyan-100 px-3 text-sm"
                        data-testid="new-user-ticket-select"
                      >
                        <option value="">Select ticket...</option>
                        {allTickets.map(t => (
                          <option key={t.ticket_id} value={t.ticket_id}>{t.ticket_id} — {t.subscriber} ({t.status})</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleCreateUser}
                  className="bg-cyan-500 hover:bg-cyan-400 text-[#0a0e14]"
                >
                  Add User
                </Button>
              </div>
            </div>

            {/* Users List */}
            <div className="space-y-3">
              <h3 className="text-sm font-stark text-cyan-300 uppercase tracking-wider">Current Users</h3>
              {users.map((user) => (
                <div
                  key={user.id}
                  className="bg-[#0a0e14] border border-cyan-500/30 rounded-lg p-4 flex items-center justify-between"
                >
                  {editingUser?.id === user.id ? (
                    <div className="flex-1 flex items-center gap-3 flex-wrap">
                      <Input
                        type="text"
                        value={editingUser.username || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                        placeholder="New username"
                        className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 w-28"
                      />
                      <div className="relative w-28">
                        <Input
                          type={showEditPassword ? "text" : "password"}
                          value={editingUser.password || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                          placeholder="New password"
                          className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 w-full pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditPassword(!showEditPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-cyan-500/50 hover:text-cyan-300 transition-colors"
                          data-testid="toggle-edit-password-visibility"
                        >
                          {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <Input
                        type="email"
                        value={editingUser.email || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                        placeholder="Email"
                        className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 w-40"
                        data-testid="edit-user-email"
                      />
                      <select
                        value={editingUser.role || user.role || 'subscriber'}
                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                        className="h-10 rounded-md border border-cyan-500/30 bg-[#0d1520] text-cyan-100 px-2 text-sm"
                      >
                        <option value="admin">Admin</option>
                        <option value="system">System</option>
                        <option value="subscriber">Subscriber</option>
                        <option value="field_tech">Field Tech</option>
                      </select>
                      <Input
                        type="text"
                        value={editingUser.subscriber_access || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, subscriber_access: e.target.value })}
                        placeholder="Subscriber (or 'all')"
                        className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 w-36"
                        data-testid="edit-user-subscriber-access"
                      />
                      <Button
                        onClick={() => handleUpdateUser(user.id)}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-400 text-[#0a0e14]"
                      >
                        Save
                      </Button>
                      <Button
                        onClick={() => setEditingUser(null)}
                        size="sm"
                        variant="outline"
                        className="border-cyan-500/40 text-cyan-400"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-cyan-100 font-medium">{user.username}</span>
                        <span className={`text-xs px-2 py-1 rounded uppercase ${
                          (user.role || (user.is_admin ? 'admin' : 'subscriber')) === 'admin' ? 'bg-purple-500/20 text-purple-300' :
                          (user.role || (user.is_admin ? 'admin' : 'subscriber')) === 'system' ? 'bg-cyan-500/20 text-cyan-300' :
                          (user.role) === 'field_tech' ? 'bg-amber-500/20 text-amber-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>
                          {user.role || (user.is_admin ? 'Admin' : 'Subscriber')}
                        </span>
                        <span className="text-xs text-cyan-500/60">
                          {user.subscriber_access === 'all' ? 'All Subscribers' : user.subscriber_access || 'All Subscribers'}
                        </span>
                        {user.email && (
                          <span className="text-xs text-cyan-500/40">{user.email}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setEditingUser({ id: user.id, username: '', password: '', email: user.email || '', role: user.role || (user.is_admin ? 'admin' : 'subscriber'), subscriber_access: user.subscriber_access || 'all' })}
                          size="sm"
                          variant="outline"
                          className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
                        >
                          Edit
                        </Button>
                        {user.username !== 'Lew' && (
                          <Button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            size="sm"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Service Tickets Section */}
            {allTickets.length > 0 && (
              <div className="mt-6 bg-[#0a0e14] border border-amber-500/30 rounded-lg p-4">
                <h3 className="text-sm font-stark text-amber-300 uppercase tracking-wider mb-3">Service Tickets</h3>
                <div className="space-y-2">
                  {allTickets.map(ticket => (
                    <div key={ticket.ticket_id} className="flex items-center justify-between bg-[#0d1520] p-3 rounded border border-amber-500/20">
                      <div>
                        <span className="text-amber-100 font-medium text-sm">{ticket.ticket_id}</span>
                        <span className="text-amber-400/70 text-xs ml-2">{ticket.subscriber}</span>
                        <span className={`text-xs ml-2 px-2 py-0.5 rounded ${
                          ticket.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                          ticket.status === 'onsite' ? 'bg-cyan-500/20 text-cyan-300' :
                          ticket.status === 'enroute' ? 'bg-amber-500/20 text-amber-300' :
                          ticket.status === 'acknowledged' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>{ticket.status.replace('_', ' ').toUpperCase()}</span>
                        {ticket.assigned_to && <span className="text-cyan-500/60 text-xs ml-2">→ {ticket.assigned_to}</span>}
                      </div>
                      <p className="text-xs text-cyan-500/50">{ticket.description?.substring(0, 50)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Field Tech Service Panel Modal */}
      {showServicePanel && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0d1520] border border-amber-500/50 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-stark text-amber-400 uppercase tracking-wider">Service Tickets</h2>
              <button onClick={() => setShowServicePanel(false)} className="text-amber-400/70 hover:text-amber-300 text-2xl">×</button>
            </div>

            {/* My Assigned Tickets */}
            {myTickets.length === 0 ? (
              <p className="text-cyan-500/50 text-sm text-center py-8">No tickets assigned to you</p>
            ) : (
              <div className="space-y-4">
                {myTickets.map(ticket => (
                  <div key={ticket.ticket_id} className="bg-[#0a0e14] border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-amber-100 font-stark text-sm">{ticket.ticket_id}</span>
                        {ticket.priority && (
                          <span className={`text-xs px-2 py-0.5 rounded uppercase ${
                            ticket.priority === 'high' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
                          }`}>{ticket.priority}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          ticket.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                          ticket.status === 'onsite' ? 'bg-cyan-500/20 text-cyan-300' :
                          ticket.status === 'enroute' ? 'bg-amber-500/20 text-amber-300' :
                          ticket.status === 'acknowledged' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>{ticket.status.replace('_', ' ').toUpperCase()}</span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {ticket.status === 'dispatched' && (
                          <Button size="sm" onClick={() => handleUpdateTicketStatus(ticket.ticket_id, 'acknowledged')}
                            className="bg-blue-600 hover:bg-blue-500 text-white text-xs" data-testid="ack-ticket-btn">
                            Acknowledge
                          </Button>
                        )}
                        {ticket.status === 'acknowledged' && (
                          <Button size="sm" onClick={() => handleUpdateTicketStatus(ticket.ticket_id, 'enroute')}
                            className="bg-amber-500 hover:bg-amber-400 text-[#0a0e14] text-xs" data-testid="enroute-ticket-btn">
                            Enroute
                          </Button>
                        )}
                        {ticket.status === 'enroute' && (
                          <Button size="sm" onClick={() => handleUpdateTicketStatus(ticket.ticket_id, 'onsite')}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs" data-testid="onsite-ticket-btn">
                            Onsite
                          </Button>
                        )}
                        {(ticket.status === 'onsite') && (
                          <Button size="sm" onClick={() => handleUpdateTicketStatus(ticket.ticket_id, 'completed')}
                            className="bg-green-600 hover:bg-green-500 text-white text-xs" data-testid="complete-ticket-btn">
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Issue & Device Info */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div>
                        <span className="text-cyan-500/60">Issue:</span>
                        <span className="text-red-300 ml-1 font-medium">{ticket.issue_type || 'Service'}</span>
                      </div>
                      <div>
                        <span className="text-cyan-500/60">Device:</span>
                        <span className="text-cyan-200 ml-1 font-medium">{ticket.device_id || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-cyan-500/60">Subscriber:</span>
                        <span className="text-cyan-300 ml-1">{ticket.subscriber}</span>
                      </div>
                      {ticket.location && (
                        <div>
                          <span className="text-cyan-500/60">Location:</span>
                          <span className="text-cyan-300 ml-1">{ticket.location.site}{ticket.location.building ? `, ${ticket.location.building}` : ''}</span>
                        </div>
                      )}
                    </div>
                    {ticket.location?.placement && (
                      <p className="text-xs text-cyan-400/50 mb-2">Placement: {ticket.location.placement}</p>
                    )}

                    <p className="text-cyan-400/80 text-xs mb-3">{ticket.description}</p>

                    {/* Parts Required */}
                    {ticket.parts_required && ticket.parts_required.length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2 mb-3">
                        <span className="text-xs text-amber-300 font-stark uppercase tracking-wider">Parts Required:</span>
                        <div className="mt-1">{ticket.parts_required.map((p, i) => (
                          <span key={i} className="text-xs text-amber-100 bg-amber-500/20 px-2 py-0.5 rounded mr-1">{p}</span>
                        ))}</div>
                      </div>
                    )}

                    {/* Log Work / Part Replacement Form */}
                    {ticket.status !== 'completed' && (
                      <div className="border-t border-amber-500/20 pt-4 mt-3">
                        <h4 className="text-xs font-stark text-amber-300 uppercase tracking-wider mb-3">Log Work</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-cyan-300/70 text-xs block mb-1">Action</label>
                            <select
                              value={deviceUpdateForm.action || ''}
                              onChange={(e) => setDeviceUpdateForm({ ...deviceUpdateForm, action: e.target.value })}
                              className="w-full h-10 rounded-md border border-cyan-500/30 bg-[#0d1520] text-cyan-100 px-3 text-sm"
                              data-testid="work-action-select"
                            >
                              <option value="">Select action...</option>
                              <option value="replaced_camera">Replaced Camera</option>
                              <option value="replaced_battery">Replaced Battery</option>
                              <option value="replaced_pads">Replaced Pads</option>
                              <option value="device_inspection">Device Inspection</option>
                              <option value="connectivity_restored">Connectivity Restored</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-cyan-300/70 text-xs block mb-1">New Part ID</label>
                            <Input
                              type="text"
                              placeholder="e.g. XYZ987"
                              value={deviceUpdateForm.new_part_id || ''}
                              onChange={(e) => setDeviceUpdateForm({ ...deviceUpdateForm, new_part_id: e.target.value })}
                              className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                              data-testid="new-part-id-input"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-cyan-300/70 text-xs block mb-1">Description</label>
                            <Input
                              type="text"
                              placeholder="e.g. Replaced camera module, verified device connectivity"
                              value={deviceUpdateForm.notes}
                              onChange={(e) => setDeviceUpdateForm({ ...deviceUpdateForm, notes: e.target.value })}
                              className="bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50"
                              data-testid="work-description-input"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end mt-3">
                          <Button
                            onClick={async () => {
                              if (!deviceUpdateForm.action) { toast.error('Select an action'); return; }
                              if (!deviceUpdateForm.notes) { toast.error('Enter a description'); return; }
                              const token = localStorage.getItem('authToken');
                              try {
                                const res = await axios.post(`${API}/tickets/${ticket.ticket_id}/work-log`, {
                                  action: deviceUpdateForm.action,
                                  description: deviceUpdateForm.notes,
                                  new_part_id: deviceUpdateForm.new_part_id || null,
                                  old_part_id: null
                                }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success(res.data.message);
                                setDeviceUpdateForm({ device_id: '', battery_expiration: '', pad_expiration: '', notes: '', action: '', new_part_id: '' });
                                // Refresh tickets
                                const ticketRes = await axios.get(`${API}/tickets/my`, { headers: { Authorization: `Bearer ${token}` } });
                                setMyTickets(ticketRes.data.tickets || []);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to log work');
                              }
                            }}
                            className="bg-amber-500 hover:bg-amber-400 text-[#0a0e14]"
                            data-testid="submit-work-log-btn"
                          >
                            Save Work Log
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Work Log */}
                    {ticket.work_log && ticket.work_log.length > 0 && (
                      <div className="border-t border-green-500/20 pt-3 mt-3">
                        <h4 className="text-xs text-green-400/80 font-stark uppercase mb-2">Work Log</h4>
                        {ticket.work_log.map((entry, i) => (
                          <div key={i} className="text-xs bg-green-500/10 border border-green-500/20 rounded p-2 mb-2">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-green-300 font-medium">{entry.action?.replace('_', ' ').toUpperCase()}</span>
                              {entry.new_part_id && <span className="text-cyan-300 bg-cyan-500/20 px-1.5 py-0.5 rounded">New Part: {entry.new_part_id}</span>}
                            </div>
                            <p className="text-cyan-300/70">{entry.description}</p>
                            <span className="text-cyan-500/40 text-[10px]">{entry.performed_by} — {new Date(entry.timestamp).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    {ticket.notes && ticket.notes.length > 0 && (
                      <div className="border-t border-cyan-500/20 pt-3 mt-3">
                        <h4 className="text-xs text-cyan-400/60 uppercase mb-2">Activity Log</h4>
                        {ticket.notes.map((note, i) => (
                          <div key={i} className="text-xs text-cyan-300/70 mb-1">
                            <span className="text-cyan-500/50">{note.author} ({new Date(note.timestamp).toLocaleString()}):</span> {note.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden h-full bg-gradient-to-b from-[#0a0e14] to-[#0d1520]">
        {/* Voice Interaction Area - Fixed, no scroll */}
        <div className="flex-1 flex flex-col items-center overflow-hidden w-full lg:w-auto">
          <div className="flex flex-col items-center space-y-0 flex-shrink-0 w-full" style={{ paddingTop: '0px' }}>
          {/* API Key Warning Banner */}
          {apiKeyConfigured === false && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-2xl bg-amber-900/30 border border-amber-500/50 rounded-lg p-4 mx-6 mt-4"
            >
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-300 font-stark">System Alert</h3>
                  <p className="text-sm text-amber-200/80 mt-1">
                    Voice module offline. Configure API key in <code className="bg-amber-900/50 px-1.5 py-0.5 rounded text-xs text-cyan-300">/app/backend/.env</code>
                  </p>
                  <Link to="/settings" className="text-sm font-medium text-cyan-400 underline mt-2 inline-block hover:text-cyan-300">
                    Access Settings →
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Status Display - Stark HUD Style */}
          <div className="text-center space-y-2" style={{ marginTop: '30px' }}>
            <motion.h2 
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-stark tracking-wider ${
                voiceState === 'idle' 
                  ? 'text-green-400' 
                  : 'text-cyan-400 text-glow-blue'
              }`}
              style={voiceState === 'idle' ? { textShadow: '0 0 20px rgba(74, 222, 128, 0.7), 0 0 40px rgba(74, 222, 128, 0.5)' } : {}}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {voiceState === 'idle' && 'READY'}
              {voiceState === 'listening' && 'RECORDING'}
              {voiceState === 'processing' && 'ANALYZING'}
              {voiceState === 'speaking' && 'RESPONDING'}
            </motion.h2>
            <p className="text-cyan-300/70 text-sm sm:text-base tracking-wide uppercase">
              {voiceState === 'idle' && (voiceMode === 'push' ? 'Hold to activate' : 'Tap Mic for voice command')}
              {voiceState === 'listening' && 'Audio input active...'}
              {voiceState === 'processing' && 'Processing query...'}
              {voiceState === 'speaking' && 'AEDA responding'}
            </p>
          </div>

          {/* Voice Wave Animation - Shows when listening */}
          {voiceState === 'listening' && (
            <motion.div 
              className="flex items-center justify-center gap-1 h-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1.5 bg-red-500 rounded-full"
                  animate={{
                    height: [8, 24 + Math.random() * 16, 8],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 0.5 + Math.random() * 0.3,
                    delay: i * 0.1,
                    ease: "easeInOut"
                  }}
                  style={{ boxShadow: '0 0 10px rgba(255, 45, 45, 0.8)' }}
                />
              ))}
            </motion.div>
          )}

          {/* Mic Button - Arc Reactor Style */}
          <motion.button
            data-testid="mic-trigger"
            className={getMicButtonStyle()}
            onClick={handleMicClick}
            onMouseDown={handleMicMouseDown}
            onMouseUp={handleMicMouseUp}
            onTouchStart={handleMicMouseDown}
            onTouchEnd={handleMicMouseUp}
            animate={getMicAnimation()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ marginTop: '20px' }}
          >
            {isMuted ? (
              <MicOff className="h-12 w-12 sm:h-14 sm:w-14" />
            ) : (
              <motion.div
                animate={voiceState === 'idle' ? micIconPulse : {}}
              >
                <Mic className="h-12 w-12 sm:h-14 sm:w-14" />
              </motion.div>
            )}
          </motion.button>

          {/* Stop Button - Visible when session is active */}
          {isSessionActive && (
            <div style={{ marginTop: '15px' }}>
              <Button
                onClick={() => {
                  console.log('Stop button clicked');
                  setIsSessionActive(false); isSessionActiveRef.current = false;
                  setVoiceState('idle');
                  setIsConnected(false);
                  toast.info('Stopped');
                  
                  // Stop the microphone
                  stopMicrophone();
                  
                  // Cancel any pending mic unmute timer
                  if (micUnmuteTimerRef.current) {
                    clearTimeout(micUnmuteTimerRef.current);
                    micUnmuteTimerRef.current = null;
                  }
                  
                  // Cancel any in-progress AI response
                  if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
                    try {
                      dataChannelRef.current.send(JSON.stringify({ type: 'response.cancel' }));
                    } catch (e) {
                      console.warn('Could not send response.cancel:', e);
                    }
                  }
                  
                  // Stop audio playback IMMEDIATELY
                  if (audioElementRef.current) {
                    audioElementRef.current.pause();
                    audioElementRef.current.srcObject = null;
                  }
                  
                  // Close the peer connection to fully reset
                  if (peerConnectionRef.current) {
                    peerConnectionRef.current.close();
                    peerConnectionRef.current = null;
                  }
                  if (dataChannelRef.current) {
                    dataChannelRef.current = null;
                  }
                }}
                variant="destructive"
                size="lg"
                className="px-8 py-6 text-lg glow-red bg-red-600 hover:bg-red-700"
                data-testid="stop-listening-button"
              >
                ⏹️ STOP
              </Button>
            </div>
          )}

          {/* Text Input - Stark Style */}
          <div className="w-full max-w-md flex gap-2" style={{ marginTop: '20px' }}>
            <Input
              data-testid="text-input"
              type="text"
              placeholder="Click Mic to speak or type here"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTextQuery()}
              className="flex-1 bg-[#0d1520] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50 focus:border-cyan-400 focus:ring-cyan-400/30"
            />
            <Button onClick={handleTextQuery} size="icon" data-testid="send-button" className="bg-cyan-500 hover:bg-cyan-400 text-[#0a0e14] glow-blue">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          </div>

          {/* Conversation Display - Stark HUD Panel - Always visible */}
          <div className="w-full max-w-2xl px-6 flex-1 overflow-hidden flex flex-col" style={{ marginTop: '15px' }}>
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <h3 className="text-sm font-semibold text-cyan-400 font-stark uppercase tracking-wider">Communications Log</h3>
              {messages.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleEmailLog}
                    disabled={isSendingEmail}
                    className="text-xs text-cyan-500/70 hover:text-cyan-300 transition-colors px-2 py-1 rounded hover:bg-cyan-500/10 uppercase tracking-wider flex items-center gap-1 disabled:opacity-40"
                    data-testid="email-log-button"
                  >
                    <Mail className="h-3 w-3" />
                    {isSendingEmail ? 'Sending...' : 'Email Log'}
                  </button>
                  <button
                    onClick={() => {
                      setMessages([]);
                      setProcessedResponseIds(new Set());
                      toast.success('Conversation cleared');
                    }}
                    className="text-xs text-cyan-500/70 hover:text-cyan-300 transition-colors px-2 py-1 rounded hover:bg-cyan-500/10 uppercase tracking-wider"
                    data-testid="clear-conversation-button"
                  >
                    Clear Log
                  </button>
                </div>
              )}
            </div>
            <div className="bg-[#0d1520]/80 border border-cyan-500/30 rounded-lg overflow-hidden flex-1 hud-border">
              <div className="overflow-y-auto overflow-x-hidden h-full p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center text-cyan-500/50 py-8">
                    <p className="text-sm uppercase tracking-wider">Awaiting voice input...</p>
                    <p className="text-xs mt-2">Tap the microphone to begin</p>
                  </div>
                ) : (
                  <AnimatePresence>
                    {messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-3 rounded-lg ${
                          message.role === 'user' 
                            ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 text-cyan-100' 
                            : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 text-amber-100'
                        } ${message.isLive ? 'opacity-70' : ''}`}
                      >
                        <p className="text-xs font-semibold mb-1 uppercase tracking-wider opacity-70">
                          {message.role === 'user' ? '🎤 You said:' : '🤖 Response:'}
                        </p>
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {renderMessageWithLinks(message.content)}
                        </p>
                        {message.imageUrl && (
                          <a href={message.imageUrl} target="_blank" rel="noopener noreferrer" className="block mt-2">
                            <img src={message.imageUrl} alt="AED device" className="max-w-full max-h-48 rounded border border-cyan-500/30" />
                          </a>
                        )}
                        {message.isLive && (
                          <span className="text-xs opacity-60">• Speaking...</span>
                        )}
                        {message.sources && message.sources.length > 0 && (
                          <p className="text-xs mt-2 opacity-70">
                            Sources: {message.sources.join(', ')}
                          </p>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
                <div ref={messagesEndRef} style={{ height: '1px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Transcript Panel - Stark HUD Style - Only visible on desktop */}
        {showTranscript && (
          <motion.div 
            className="hidden lg:flex lg:w-96 bg-[#0a0e14] border-l border-cyan-500/30 flex-col h-full overflow-hidden relative"
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
          >
            {/* HUD corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-400/50"></div>
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyan-400/50"></div>
            
            <div className="p-4 border-b border-cyan-500/30 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-cyan-500/5 to-transparent">
              <h3 className="font-semibold text-cyan-400 font-stark uppercase tracking-wider text-sm">Transcript</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setMessages([]);
                    setProcessedResponseIds(new Set());
                    toast.success('Conversation cleared');
                  }}
                  className="text-xs text-cyan-500/70 hover:text-cyan-300 transition-colors px-2 py-1 rounded hover:bg-cyan-500/10 uppercase tracking-wider"
                  data-testid="clear-conversation-button-desktop"
                >
                  Clear
                </button>
                <button
                  onClick={() => setShowTranscript(false)}
                  className="text-xs text-cyan-500/70 hover:text-cyan-300 uppercase tracking-wider"
                >
                  Hide
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-gradient-to-b from-[#0a0e14] to-[#0d1520]" data-testid="transcript-view">
              <div className="space-y-4">
                <AnimatePresence>
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg ${
                        message.role === 'user' 
                          ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 text-cyan-100 ml-4' 
                          : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 text-amber-100 mr-4'
                      } ${message.isLive ? 'opacity-70' : ''}`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {renderMessageWithLinks(message.content)}
                      </p>
                      {message.imageUrl && (
                        <a href={message.imageUrl} target="_blank" rel="noopener noreferrer" className="block mt-2">
                          <img src={message.imageUrl} alt="AED device" className="max-w-full max-h-32 rounded border border-cyan-500/30" />
                        </a>
                      )}
                      {message.isLive && (
                        <span className="text-xs text-cyan-400/60">● Processing...</span>
                      )}
                      {message.sources && message.sources.length > 0 && (
                        <p className="text-xs mt-2 text-cyan-400/50">
                          Source: {message.sources.join(', ')}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}

        {/* Show transcript button - Stark Style - Only on desktop when hidden */}
        {!showTranscript && messages.length > 0 && (
          <button
            onClick={() => setShowTranscript(true)}
            className="hidden lg:block fixed bottom-4 right-4 bg-cyan-500 text-[#0a0e14] px-6 py-3 rounded-full shadow-lg hover:bg-cyan-400 transition-colors z-40 text-sm font-semibold uppercase tracking-wider glow-blue"
            data-testid="show-transcript-button"
          >
            📡 Show Transcript
          </button>
        )}
      </div>
    </div>
  );
};

export default VoiceInterface;